import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight, Layout, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { TEMPLATES } from "@/data/content";

const PERKS = ["Custom-designed for your industry", "Mobile-first & lightning fast", "Built to convert visitors into leads", "SEO-ready from day one"];

export default function Templates() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [selected, setSelected] = useState(0);

  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);
  const prev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    const id = setInterval(() => emblaApi.scrollNext(), 4500);
    return () => { clearInterval(id); emblaApi.off("select", onSelect); };
  }, [emblaApi]);

  return (
    <div data-testid="templates-page">
      <section className="relative overflow-hidden pt-40 pb-12">
        <div className="pointer-events-none absolute inset-0 grid-bg opacity-60" />
        <div className="pointer-events-none absolute -right-32 top-24 h-96 w-96 rounded-full bg-brand/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-brand backdrop-blur">
              <Layout className="h-3.5 w-3.5" /> Web Design Templates
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 max-w-4xl font-display text-5xl font-black leading-[0.95] tracking-tighter text-slate-900 sm:text-6xl lg:text-7xl">
              Websites built to grow<br /><span className="text-brand">any</span> business.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
              Browse a few of the industries we design for. Every site is custom-tailored to the business — these are just a taste of what we can build for you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 pb-16">
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="overflow-hidden rounded-3xl" ref={emblaRef}>
            <div className="flex">
              {TEMPLATES.map((t, i) => (
                <div key={t.business} className="min-w-0 flex-[0_0_100%] px-2 md:flex-[0_0_72%] lg:flex-[0_0_60%]">
                  <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-900">
                    <img src={t.image} alt={`${t.business} website design`} className="h-[340px] w-full object-cover sm:h-[460px] lg:h-[520px]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/40 to-transparent" />
                    <div className="absolute left-0 top-6">
                      <span className="rounded-r-full bg-brand px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-white">{t.tag}</span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-7 lg:p-10">
                      <h3 className="font-display text-3xl font-black tracking-tight text-white lg:text-4xl">{t.business}</h3>
                      <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-200">{t.blurb}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <button data-testid="carousel-prev" onClick={prev} aria-label="Previous" className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-900 transition-colors hover:border-brand hover:text-brand">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              {TEMPLATES.map((_, i) => (
                <button
                  key={i}
                  data-testid={`carousel-dot-${i}`}
                  onClick={() => scrollTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-2.5 rounded-full transition-all ${selected === i ? "w-8 bg-brand" : "w-2.5 bg-slate-300 hover:bg-slate-400"}`}
                />
              ))}
            </div>
            <button data-testid="carousel-next" onClick={next} aria-label="Next" className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-900 transition-colors hover:border-brand hover:text-brand">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      <section className="relative z-10 pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PERKS.map((perk, i) => (
              <Reveal key={perk} delay={i * 0.08}>
                <div className="flex h-full items-start gap-3 rounded-2xl border border-slate-200 bg-white p-6">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-white"><Check className="h-3.5 w-3.5" /></span>
                  <p className="text-sm font-medium text-slate-800">{perk}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 pb-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-3xl navy-mesh px-8 py-16 text-center text-white lg:px-20">
            <h2 className="font-display text-3xl font-black tracking-tighter sm:text-5xl">Don't see your industry?</h2>
            <p className="mx-auto mt-4 max-w-xl text-white/70">We design custom websites for every kind of business. Tell us about yours and we'll mock up a plan for free.</p>
            <Link to="/contact" data-testid="templates-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-bold text-white transition-colors hover:bg-brand-hover">
              Get my custom website <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
